import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ViewChild, computed, effect, inject, signal, PLATFORM_ID, ɵgetDirectives as getDirectives } from '@angular/core';
import { IconKeys } from '@powerfleet/shared/common/enums';
import { ToggleContainersService } from '@powerfleet/shared/services/internal';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { MenuItem } from '@progress/kendo-angular-menu';
import { SplitterModule } from '@progress/kendo-angular-layout';
import { appSections, type AppSection } from './app-navigation';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, lastValueFrom, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AuraChatComponent,
  AuraUiChatAgent,
  AuraUiChatAnalyticsEvent,
  AuraUiChatConfig,
  AuraUiChatRuntimeResponseEvent,
} from '@powerfleet/aura_standalone.ui';
import { ThemeService } from './theme.service';
import { GlobalBannerService } from '@powerfleet/shared/components/banner';
import { KENDO_TOOLTIP, TooltipDirective } from '@progress/kendo-angular-tooltip';
import { BrowserEnvironmentService } from './browser-environment.service';
import { HostApplicationService } from './services/host-application.service';
import { BaseAppContext, HostAppContext, HostPlatform } from './services/host-application.interfaces';
import { AuraChatBridgeService, AuraChatSendRequest } from './services/aura-chat-bridge.service';
import { GainsightAnalyticsService } from './services/analytics/gainsight-analytics.service';
import { AuraAgentsService, AuraAgentType } from './services/aura-agents.service';
import { AuraPageContextService, withItemInView } from './services/aura-page-context.service';

type AppMenuItem = MenuItem & {
  data: AppSection;
  url: string;
};

const AURA_PANEL_STORAGE_KEY = 'app-aura-panel-open';

// Aura agent id for each host platform Insights can be embedded in. The agent selector is
// hidden in this host, so the agent is resolved from the platform rather than user choice.
const PLATFORM_AGENT_IDS: Record<HostPlatform, string> = {
  'on-road': 'on_road',
  'on-site': 'on_site',
};

// Wire-contract key the server reads the page context from.
const PAGE_CONTEXT_KEY = 'enterprise_insights.unityaiot.com';

@Component({
  selector: 'app-root',
  imports: [
    ButtonModule,
    SplitterModule,
    KENDO_TOOLTIP,
    RouterOutlet,
    AuraChatComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  host: {
    // Expose the current theme as a host attribute so CSS variables can switch themes.
    '[attr.data-theme]': 'theme()',
    '[class.library-route]': 'isLibraryRoute()',
  },
})
export class App implements AfterViewInit {
  iconKeys = IconKeys;
  
  // Theme state and persistence live in the shared service.
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browserEnvironment = inject(BrowserEnvironmentService);
  private readonly toggleContainersService = inject(ToggleContainersService);
  private readonly hostApplicationService = inject(HostApplicationService);
  private readonly auraChatBridge = inject(AuraChatBridgeService);
  private readonly gainsightAnalyticsService = inject(GainsightAnalyticsService);
  private readonly auraAgentsService = inject(AuraAgentsService);
  private readonly auraPageContextService = inject(AuraPageContextService);
  private readonly platformId = inject(PLATFORM_ID);

  // The runtime lives inside an @defer block, so it only exists once the panel
  // is open. The setter flushes any message queued while it was still absent.
  private auraRuntime?: AuraChatComponent;
  private pendingAuraRequest: AuraChatSendRequest | null = null;

  @ViewChild(AuraChatComponent)
  protected set auraRuntimeRef(ref: AuraChatComponent | undefined) {
    this.auraRuntime = ref;
    if (ref) {
      this.flushPendingAuraRequest();
    }
  }
  protected readonly menuItems: AppMenuItem[] = appSections.map((section) => ({
    text: section.title,
    data: section.path,
    url: section.url,
  }));

  #globalBannerService = inject(GlobalBannerService);
  readonly isBannerVisible = this.#globalBannerService.isOpen;

  protected readonly isPlatformBrowser = isPlatformBrowser(this.platformId);
  protected readonly title = signal('warehouse-insights');
  protected readonly theme = this.themeService.theme;
  protected readonly isLibraryRoute = signal(this.isLibraryBreadcrumbRoute(this.router.url));
  protected readonly auraContainerState = this.toggleContainersService.getContainerState('aura');
  // Controls whether the Aura splitter pane is rendered.
  protected readonly auraPanelOpen = computed(() => this.auraContainerState().opened);

  // Agents available from the Aura backend; empty until the agents call completes.
  private readonly availableAgents = signal<AuraAgentType[]>([]);

  // The agent for the current host platform, derived from the backend agents list.
  // Null until agents load, or when the host platform / its agent cannot be resolved.
  protected readonly selectedAgent = computed<AuraUiChatAgent | null>(() => {
    const agents = this.availableAgents();
    if (agents.length === 0) {
      return null;
    }

    const platform = this.hostApplicationService.computedPlatform();
    if (!platform) {
      console.error('Cannot resolve Aura agent: host platform is unmapped or missing');
      return null;
    }

    const agentId = PLATFORM_AGENT_IDS[platform];
    const match = agents.find((agent) => agent.id === agentId);
    if (!match) {
      console.error(`Cannot resolve Aura agent: backend returned no "${agentId}" agent`);
      return null;
    }

    return {
      id: match.id,
      name: match.name,
      description: match.userFacingDescription || match.description,
      // Bundled Aura logo shipped in the package assets (copied to /aura-assets via angular.json).
      avatarUrl: 'aura-assets/images/logo-aura.svg',
    };
  });

  protected readonly chatConfig: AuraUiChatConfig = {
    showSuggestions: true,
    showReferences: true,
    showAgentSelector: false,
    showThemeSelector: false,
    showChatInputKbSources: false,
    showChatInputPreferences: true,
    allowToggleTempChat: true,
  };

  protected readonly isThemeDark = computed(() => this.theme() === 'dark');
  protected readonly auraCollapsed = signal(false);

  /** Latest context supplied by the embedding host, held separately so a page-context update cannot overwrite it. */
  private readonly hostContext = signal<HostAppContext | undefined>(undefined);

  /**
   * Context sent with every chat message: the host sections plus the page context.
   *
   * Computed rather than written to, so a host update never discards the page context and a page
   * update never discards the host sections. The page key is always emitted because the server
   * treats an empty list and an absent key alike, which keeps the merge branch-free.
   */
  protected readonly chatContext = computed<Record<string, unknown>>(() => ({
    ...withItemInView(this.hostContext(), this.auraPageContextService.itemInView()),
    [PAGE_CONTEXT_KEY]: this.auraPageContextService.pageContext(),
  }));

  private readonly auraBaseContext = computed<BaseAppContext | undefined>(
    () => this.hostContext()?.['embedded.aura.unityaiot.com'],
  );

  /** Active chat language sourced from the host context (falls back to the package default). */
  protected readonly language = computed<string | null>(() => this.auraBaseContext()?.language ?? null);

  /** Filter the conversation-history list to this platform. */
  protected readonly historyHost = computed<string | null>(() => this.auraBaseContext()?.context_host ?? null);

  /** Current user id from the host context (drives local message attribution, mirroring the host app). */
  protected readonly auraUserId = computed<string | null>(() => this.auraBaseContext()?.user_id ?? null);

  /** Host-owned properties merged into every analytics event the runtime emits. */
  protected readonly analyticsContext = computed<Record<string, string | number | boolean>>(() => ({
    host: this.auraBaseContext()?.context_host ?? 'warehouse-insights',
  }));

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isLibraryRoute.set(this.isLibraryBreadcrumbRoute(this.router.url));
      });

    if (this.isPlatformBrowser && this.getInitialAuraPanelState() !== this.auraPanelOpen()) {
      this.toggleContainersService.toggleContainer('aura');
    }

    this.hostApplicationService.onContextUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((context) => {
        this.applyContext(context);
        this.gainsightAnalyticsService.init().catch((err) => {
          console.error('Failed to initialize Gainsight Analytics', err);
        });
      });

    this.auraChatBridge.requests$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((request) => this.handleAuraSendRequest(request));

    if (this.isPlatformBrowser) {
      void this.initialiseHostIntegration();
      // Runs after the first navigation so the route guard has captured any ?token= value.
      this.router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          take(1),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => void this.loadAvailableAgents());
    }

    effect(() => {
      if (!this.isPlatformBrowser) {
        return;
      }

      const isOpen = this.auraPanelOpen();

      this.setStoredAuraPanelState(isOpen);
      queueMicrotask(() => this.syncAuraButtonActiveState(isOpen));
    });
  }

  private applyContext(context: HostAppContext | undefined): void {
    this.hostContext.set(context);
  }

  private handleAuraSendRequest(request: AuraChatSendRequest): void {
    this.pendingAuraRequest = request;

    // Expand the Aura splitter pane if it is currently collapsed.
    this.auraCollapsed.set(false);

    // If the runtime is already present, send immediately.
    this.flushPendingAuraRequest();
  }

  private flushPendingAuraRequest(): void {
    const request = this.pendingAuraRequest;
    if (!request || !this.auraRuntime) {
      return;
    }
    this.pendingAuraRequest = null;
    this.auraRuntime.sendMessage(request.message, { newConversation: request.newConversation });
  }

  private async initialiseHostIntegration(): Promise<void> {
    if (!this.hostApplicationService.isEmbedded) {
      return;
    }

    await Promise.all([
      this.hostApplicationService.loadContext(),
      this.hostApplicationService.getUserToken(),
    ]);

    this.applyContext(this.hostApplicationService.getCurrentContext());
  }

  /**
   * Load the available Aura agents from the backend. The selected agent is derived
   * reactively from this list and the host platform. Never throws — leaves the list
   * empty on failure so the runtime falls back to its own default.
   */
  private async loadAvailableAgents(): Promise<void> {
    try {
      const agents = await lastValueFrom(this.auraAgentsService.getAvailableAgents());
      this.availableAgents.set(agents);
    } catch (error) {
      console.error('Failed to resolve Aura agent', error);
    }
  }

  protected onAiResponse(event: AuraUiChatRuntimeResponseEvent): void {
    console.log('Aura response received', event);
  }

  protected onAiError(error: unknown): void {
    console.error('Aura chat runtime error', error);
  }

  /** Forward the runtime's semantic analytics events to Gainsight PX. */
  protected onAnalyticsEvent(event: AuraUiChatAnalyticsEvent): void {
    this.gainsightAnalyticsService.trackEvent(event.name, event.properties);
  }

  /** Open a clicked citation/reference link in a new tab. */
  protected onReferenceClick(link: string): void {
    if (this.isPlatformBrowser && /^https?:\/\//i.test(link)) {
      // noreferrer also strips the Referer header so external sites cannot see the app URL.
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  }

  ngAfterViewInit(): void {
    if (!this.isPlatformBrowser) {
      return;
    }

    // Sync the persisted panel state onto the button after the view is ready.
    queueMicrotask(() => this.syncAuraButtonActiveState());
  }

  protected toggleTheme(): void {
    // Delegate theme state changes to the shared service.
    this.themeService.toggleTheme();
  }

  protected toggleThemeWithTooltip(tooltip: TooltipDirective, anchor: EventTarget | null): void {
    const tooltipWasOpen = Boolean(tooltip.popupRef);

    this.toggleTheme();

    if (!tooltipWasOpen || !(anchor instanceof Element)) {
      return;
    }

    // Re-open an already visible tooltip so it re-reads the updated title immediately.
    queueMicrotask(() => {
      tooltip.hide();
      tooltip.show(anchor);
    });
  }

  private getInitialAuraPanelState(): boolean {
    return this.browserEnvironment.getStorageItem(AURA_PANEL_STORAGE_KEY) === 'true';
  }

  private setStoredAuraPanelState(isOpen: boolean): void {
    this.browserEnvironment.setStorageItem(AURA_PANEL_STORAGE_KEY, String(isOpen));
  }

  private syncAuraButtonActiveState(activeState = this.auraPanelOpen()): void {
    const auraButtonHost = this.browserEnvironment.querySelector<HTMLElement>('shared-secondary-nav shared-aura-button');

    if (!auraButtonHost) {
      return;
    }

    const auraButton = getDirectives(auraButtonHost).find(
      (directive: unknown): directive is { isActive?: { set: (value: boolean) => void } } =>
        Boolean(directive) && typeof (directive as { isActive?: unknown }).isActive === 'function'
    );

    if (auraButton?.isActive) {
      auraButton.isActive.set(activeState);
      return;
    }

    const auraToggleButton = auraButtonHost.querySelector<HTMLButtonElement>('.aura-wrapper');

    if (auraToggleButton) {
      auraToggleButton.classList.toggle('active', activeState);
    }
  }

  private isLibraryBreadcrumbRoute(url: string): boolean {
    return url.startsWith('/library');
  }
}
